import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Spin,
  Form,
  Card,
  Select,
  DatePicker,
  Input,
  Dropdown,
  Tabs,
  Table,
  Tag,
  Empty,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import { connect } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";

import MainLayout from "../../../components/layouts/enhancement/MainLayout";
import TopContent from "../../../components/layouts/enhancement/TopContent";
import ChevronDown from "@untitled-ui/icons-react/build/esm/ChevronDown";
import Settings01 from "@untitled-ui/icons-react/build/esm/Settings01";
import cnt from "../../../services/definedConstant";
import { rightAction } from "../../../redux/actions";
import {
  labelConfig,
  staticConfig,
  apiConfig,
} from "../treasuryContractBooking.config";
import { BOM } from "../../../services/definedMethod";

// Converts [2026, 3, 16] → dayjs object for DatePicker
const parseDateArray = (arr) => {
  if (!Array.isArray(arr) || arr.length < 3) return null;
  const [y, m, d] = arr;
  return dayjs(
    `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`,
  );
};

const CURRENCY_OPTIONS = [
  { label: "NGN – Nigerian Naira", value: "NGN" },
  { label: "USD – US Dollar", value: "USD" },
  { label: "EUR – Euro", value: "EUR" },
  { label: "GBP – British Pound", value: "GBP" },
];

const STATUS_TO_DISABLED_ACTION = {
  Draft: "open_auction",
  Open: "open_auction",
  Closed: "close_auction",
  Published: "publish_auction",
  Cancelled: "cancel_auction",
};

const statusColor = (status) => {
  const map = {
    Open: "green",
    Closed: "red",
    Draft: "default",
    Published: "blue",
    Cancelled: "volcano",
    Submitted: "geekblue",
    Pending: "orange",
    Accepted: "cyan",
    Rejected: "magenta",
  };
  return map[status] ?? "default";
};

const AuctionLineTab = ({ auctionId, session, apiConfig }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!auctionId) return;
    setLoading(true);
    BOM.FetchReqAction(
      { session },
      `${apiConfig.GetAuctions}/auctions/${auctionId}/lines`,
      (err, res) => {
        setLoading(false);
        if (!err && Array.isArray(res)) setData(res);
      },
    );
  }, [auctionId]);

  const columns = [
    {
      title: "Security Name",
      dataIndex: "securityInstrumentName",
      key: "securityInstrumentName",
      render: (v) => v ?? "—",
    },
    {
      title: "Short Name",
      dataIndex: "securityShortName",
      key: "securityShortName",
      render: (v) => v ?? "—",
    },
    {
      title: "Offer Amount",
      dataIndex: "offerAmount",
      key: "offerAmount",
      align: "right",
      render: (v) => (v != null ? v.toLocaleString() : "—"),
    },
    {
      title: "Min Bid Amount",
      dataIndex: "minBidAmount",
      key: "minBidAmount",
      align: "right",
      render: (v) => (v != null ? v.toLocaleString() : "—"),
    },
    {
      title: "Max Bid Amount",
      dataIndex: "maxBidAmount",
      key: "maxBidAmount",
      align: "right",
      render: (v) => (v != null ? v.toLocaleString() : "—"),
    },
    {
      title: "Tenor (Days)",
      dataIndex: "tenorDays",
      key: "tenorDays",
      align: "center",
      render: (v) => (v != null ? `${v} days` : "—"),
    },
    {
      title: "Bid Quote Type",
      dataIndex: "bidQuoteTypeDescription",
      key: "bidQuoteTypeDescription",
      render: (v) => v ?? "—",
    },
    {
      title: "Maturity Date",
      dataIndex: "maturityDate",
      key: "maturityDate",
      render: (v) =>
        Array.isArray(v) ? parseDateArray(v)?.format("YYYY-MM-DD") : (v ?? "—"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v) =>
        v ? (
          <Tag color={statusColor(v)} style={{ textTransform: "capitalize" }}>
            {v}
          </Tag>
        ) : (
          <span>—</span>
        ),
    },
  ];

  return (
    <Table
      loading={loading}
      dataSource={data}
      columns={columns}
      rowKey={(r) => r.auctionLineId ?? Math.random()}
      pagination={{ pageSize: 8, showSizeChanger: false }}
      locale={{ emptyText: <Empty description="No auction lines found" /> }}
      scroll={{ x: "max-content" }}
      style={{ marginTop: 4 }}
    />
  );
};

const AuctionBidTab = ({
  auctionId,
  session,
  apiConfig,
  refreshKey,
  onSubmitBid,
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submittingId, setSubmittingId] = useState(null);

  useEffect(() => {
    if (!auctionId) return;
    fetchBids();
  }, [auctionId, refreshKey]);

  const fetchBids = () => {
    setLoading(true);
    BOM.FetchReqAction(
      { session },
      `${apiConfig.GetAuctions}/auctions/${auctionId}/bids`,
      (err, res) => {
        setLoading(false);
        if (!err && Array.isArray(res)) setData(res);
      },
    );
  };

  const handleSubmitBid = (bidId) => {
    setSubmittingId(bidId);
    onSubmitBid?.(bidId, () => {
      setSubmittingId(null);
      fetchBids(); // refresh rows after submit
    });
  };

  const columns = [
    {
      title: "Bid ID",
      dataIndex: "bidId",
      key: "bidId",
      render: (v) => v ?? "—",
    },
    {
      title: "Auction Line ID",
      dataIndex: "auctionLineId",
      key: "auctionLineId",
      render: (v) => v ?? "—",
    },
    {
      title: "Bid Type",
      dataIndex: "bidTypeDescription",
      key: "bidTypeDescription",
      render: (v) => v ?? "—",
    },
    {
      title: "Bid Amount",
      dataIndex: "bidAmount",
      key: "bidAmount",
      align: "right",
      render: (v) => (v != null ? v.toLocaleString() : "—"),
    },
    {
      title: "Bid Rate (%)",
      dataIndex: "bidRate",
      key: "bidRate",
      align: "right",
      render: (v) => (v != null ? `${v}%` : "—"),
    },
    {
      title: "Status",
      dataIndex: "statusDescription",
      key: "statusDescription",
      render: (v) =>
        v ? <Tag color={statusColor(v)}>{v}</Tag> : <span>—</span>,
    },
    {
      title: "",
      key: "action",
      width: 140,
      render: (_, record) => {
        const isSubmitted =
          record.statusDescription?.toLowerCase() === "submitted";
        if (isSubmitted) return null;

        const isSubmitting = submittingId === record.bidId;
        return (
          <button
            type="button"
            disabled={isSubmitting}
            onClick={() => handleSubmitBid(record.bidId)}
            style={{
              height: 32,
              padding: "0 14px",
              borderRadius: 6,
              border: "1px solid #BA24D5",
              background: isSubmitting ? "#f3e8fd" : "#fff",
              color: "#BA24D5",
              fontWeight: 600,
              fontSize: 13,
              cursor: isSubmitting ? "not-allowed" : "pointer",
              whiteSpace: "nowrap",
              opacity: isSubmitting ? 0.7 : 1,
              transition: "all 0.2s",
            }}
          >
            {isSubmitting ? "Submitting…" : "Submit Bid"}
          </button>
        );
      },
    },
  ];

  return (
    <Table
      loading={loading}
      dataSource={data}
      columns={columns}
      rowKey={(r) => r.bidId}
      pagination={{ pageSize: 8, showSizeChanger: false }}
      locale={{ emptyText: <Empty description="No bids found for this auction" /> }}
      scroll={{ x: "max-content" }}
      style={{ marginTop: 4 }}
    />
  );
};

const AuctionResultsTab = ({ auctionId, session, apiConfig }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!auctionId) return;
    setLoading(true);
    BOM.FetchReqAction(
      { session },
      `${apiConfig.GetAuctions}/auctions/${auctionId}/results`,
      (err, res) => {
        setLoading(false);
        if (!err && Array.isArray(res)) setData(res);
      },
    );
  }, [auctionId]);

  const columns = [
    {
      title: "Result ID",
      dataIndex: "resultId",
      key: "resultId",
      render: (v) =>
        v ? (
          <span
            style={{
              fontFamily: "monospace",
              fontSize: 12,
              color: "#667085",
            }}
          >
            {v.slice(0, 8)}…
          </span>
        ) : (
          "—"
        ),
    },
    {
      title: "Auction Line ID",
      dataIndex: "auctionLineId",
      key: "auctionLineId",
      align: "center",
      render: (v) => v ?? "—",
    },
    {
      title: "Total Offered",
      dataIndex: "totalOffered",
      key: "totalOffered",
      align: "right",
      render: (v) => (v != null ? v.toLocaleString() : "—"),
    },
    {
      title: "Total Bid",
      dataIndex: "totalBid",
      key: "totalBid",
      align: "right",
      render: (v) => (v != null ? v.toLocaleString() : "—"),
    },
    {
      title: "Total Allotted",
      dataIndex: "totalAllotted",
      key: "totalAllotted",
      align: "right",
      render: (v) => (v != null ? v.toLocaleString() : "—"),
    },
    {
      title: "Stop Rate",
      dataIndex: "stopRate",
      key: "stopRate",
      align: "right",
      render: (v) => (v != null ? v.toLocaleString() : "—"),
    },
  ];

  return (
    <Table
      loading={loading}
      dataSource={data}
      columns={columns}
      rowKey={(r) => r.resultId ?? Math.random()}
      pagination={{ pageSize: 8, showSizeChanger: false }}
      locale={{
        emptyText: <Empty description="No recorded results for this auction" />,
      }}
      scroll={{ x: "max-content" }}
      style={{ marginTop: 4 }}
    />
  );
};

const AllotmentTab = ({ data }) => {
  const columns = [
    {
      title: "Allotment ID",
      dataIndex: "allotmentId",
      key: "allotmentId",
      render: (v) => v ?? "—",
    },
    {
      title: "Auction Line ID",
      dataIndex: "auctionLineId",
      key: "auctionLineId",
      align: "center",
      render: (v) => v ?? "—",
    },
    {
      title: "Bid ID",
      dataIndex: "bidId",
      key: "bidId",
      align: "center",
      render: (v) => v ?? "—",
    },
    {
      title: "Allotted Amount",
      dataIndex: "allottedAmount",
      key: "allottedAmount",
      align: "right",
      render: (v) => (v != null ? v.toLocaleString() : "—"),
    },
    {
      title: "Allotted Rate (%)",
      dataIndex: "allottedRate",
      key: "allottedRate",
      align: "right",
      render: (v) => (v != null ? `${v}%` : "—"),
    },
    {
      title: "Allotment Price",
      dataIndex: "allotmentPrice",
      key: "allotmentPrice",
      align: "right",
      render: (v) => (v != null ? v.toLocaleString() : "—"),
    },
    {
      title: "Allotment Yield (%)",
      dataIndex: "allotmentYield",
      key: "allotmentYield",
      align: "right",
      render: (v) => (v != null ? `${v}%` : "—"),
    },
    {
      title: "Allotment Date",
      dataIndex: "allotmentDate",
      key: "allotmentDate",
      render: (v) =>
        Array.isArray(v) ? parseDateArray(v)?.format("YYYY-MM-DD") : (v ?? "—"),
    },
    {
      title: "Settlement Date",
      dataIndex: "settlementDate",
      key: "settlementDate",
      render: (v) =>
        Array.isArray(v) ? parseDateArray(v)?.format("YYYY-MM-DD") : (v ?? "—"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v) =>
        v ? (
          <Tag color={statusColor(v)} style={{ textTransform: "capitalize" }}>
            {v}
          </Tag>
        ) : (
          <span>—</span>
        ),
    },
  ];

  return (
    <Table
      dataSource={data}
      columns={columns}
      rowKey={(r) => r.allotmentId ?? Math.random()}
      pagination={{ pageSize: 8, showSizeChanger: false }}
      locale={{ emptyText: <Empty description="No allotments found" /> }}
      scroll={{ x: "max-content" }}
      style={{ marginTop: 4 }}
    />
  );
};

const ViewAuction = (props) => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [auctionStatus, setAuctionStatus] = useState(null);
  const [bidId, setBidId] = useState(null);
  const [activeTab, setActiveTab] = useState("auction_lines");
  const [allotmentData, setAllotmentData] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const auctionId = location.state;
  const { userData } = props;
  const { session } = userData || {};

  useEffect(() => {
    if (auctionId) fetchAuctionDetail(auctionId);
    if (auctionId) convertAllotment(auctionId);
  }, [auctionId]);

  const convertAllotment = (id) => {
    setIsLoading(true);
    const body = { session };
    const url = `${apiConfig.GetAuctions}/auctions/${id}/allotments`;

    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.defaultUserMessage || "Failed to load auction details.",
        );
        console.log(res);
        setIsLoading(false);
        return;
      }
      if (Array.isArray(res)) setAllotmentData(res);
      setIsLoading(false);
    });
  };

  const fetchAuctionDetail = (id) => {
    setIsLoading(true);
    const body = { session };
    const url = `${apiConfig.GetAuctions}/auctions/${id}`;

    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.defaultUserMessage || "Failed to load auction details.",
        );
        setIsLoading(false);
        return;
      }

      setAuctionStatus(res.statusDescription);
      if (res.bidId) setBidId(res.bidId);

      form.setFieldsValue({
        auctionCode: res.auctionCode,
        instrumentType: res.instrumentType,
        currencyCode: res.currencyCode,
        issuingAuthority: res.issuingAuthority,
        statusDescription: res.statusDescription,
        auctionDate: parseDateArray(res.auctionDate),
        settlementDate: parseDateArray(res.settlementDate),
        maturityDate: res.maturityDate ? parseDateArray(res.maturityDate) : null,
        totalOffered: res.totalOffered ?? null,
        productId: res.productId ?? null,
        securityMasterId: res.securityMasterId ?? null,
      });

      setIsLoading(false);
    });
  };

  const runApiAction = (
    urlSuffix,
    successMsg,
    method = "POST",
    urlOverride = null,
  ) => {
    setIsLoading(true);
    const body = { session, method };
    const url =
      urlOverride ??
      `${apiConfig.GetAuctions}/auctions/${auctionId}/${urlSuffix}`;

    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.errors?.[0]?.defaultUserMessage ||
            err?.defaultUserMessage ||
            "Action failed. Please try again.",
        );
        setIsLoading(false);
        return;
      }
      BOM.AlertMsg(cnt.SUCCESS, successMsg);
      setIsLoading(false);
      fetchAuctionDetail(auctionId);
    });
  };

  const handleAction = ({ key }) => {
    switch (key) {
      case "open_auction":
        runApiAction("open", "Auction opened successfully.");
        break;
      case "close_auction":
        runApiAction("close", "Auction closed successfully.");
        break;
      case "publish_auction":
        runApiAction("publish-results", "Auction published successfully.");
        break;
      case "cancel_auction":
        runApiAction("cancel", "Auction cancelled successfully.");
        break;
      case "add_auction_line":
        navigate(`/add-auction-line`, { state: auctionId });
        break;
      case "create_auction_bid":
        navigate(`/create-auction-bid`, { state: auctionId });
        break;
      case "record_auction_results":
        navigate(`/record-auction-result`, { state: auctionId });
        break;
      case "create_auction_allotment":
        navigate(`/create-auction-allotment`, { state: auctionId });
        break;
      case "convert_allotment_to_trade":
        navigate(`/auction/convert-allotment`, { state: auctionId });
        break;
      default:
        break;
    }
  };

  const handleSubmitBid = (bidId, onDone) => {
    setIsLoading(true);
    const body = { session, method: "PUT" };
    const url = `${apiConfig.GetAuctions}/auctions/${auctionId}/bids/${bidId}/submit`;

    BOM.FetchReqAction(body, url, (err) => {
      setIsLoading(false);
      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.errors?.[0]?.defaultUserMessage ||
            err?.defaultUserMessage ||
            "Failed to submit bid. Please try again.",
        );
        onDone?.();
        return;
      }
      BOM.AlertMsg(cnt.SUCCESS, "Bid submitted successfully.");
      onDone?.();
    });
  };

  const disabledKey = STATUS_TO_DISABLED_ACTION[auctionStatus] ?? null;

  const menuItem = (key, label, danger = false) => {
    const isDisabled = key === disabledKey;
    return {
      key,
      disabled: isDisabled,
      label: (
        <span
          style={{
            color: isDisabled ? "#D0D5DD" : danger ? "#B42318" : undefined,
            cursor: isDisabled ? "not-allowed" : "pointer",
          }}
        >
          {label}
        </span>
      ),
    };
  };

  const actionMenuItems = [
    menuItem("open_auction", "Open Auction"),
    menuItem("close_auction", "Close Auction"),
    menuItem("publish_auction", "Publish Auction Results"),
    { type: "divider" },
    menuItem("cancel_auction", "Cancel Auction", true),
    { type: "divider" },
    menuItem("add_auction_line", "Add Auction Line"),
    menuItem("create_auction_bid", "Create Auction Bid"),
    menuItem("record_auction_results", "Record Auction Results"),
    menuItem("create_auction_allotment", "Create Auction Allotment"),
    menuItem("convert_allotment_to_trade", "Convert Allotment to Trade"),
  ];

  const ds = { height: 44, background: "#F9FAFB", cursor: "not-allowed" };

  const Tip = ({ text, tip }) => (
    <span>
      {text}
      <Tooltip title={tip}>
        <InfoCircleOutlined style={{ marginLeft: 4, color: "#667085" }} />
      </Tooltip>
    </span>
  );

  const tabItems = [
    {
      key: "auction_lines",
      label: "Auction Lines",
      children: (
        <AuctionLineTab
          auctionId={auctionId}
          session={session}
          apiConfig={apiConfig}
        />
      ),
    },
    {
      key: "auction_bids",
      label: "Auction Bids",
      children: (
        <AuctionBidTab
          auctionId={auctionId}
          session={session}
          apiConfig={apiConfig}
          onSubmitBid={handleSubmitBid}
        />
      ),
    },
    {
      key: "auction_results",
      label: "Recorded Auction Results",
      children: (
        <AuctionResultsTab
          auctionId={auctionId}
          session={session}
          apiConfig={apiConfig}
        />
      ),
    },
    {
      key: "allotment",
      label: "Allotment",
      children: <AllotmentTab data={allotmentData} />,
    },
  ];

  return (
    <MainLayout pageItems={staticConfig.viewAuction}>
      <Spin spinning={isLoading} size="large">
        <div style={{ padding: "24px" }}>
          <Card
            style={{
              boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
              borderRadius: 20,
            }}
          >
            <Row
              justify="space-between"
              align="middle"
              style={{ marginBottom: 24 }}
            >
              <Col>
                <h3 style={{ margin: 0, fontWeight: 600 }}>Auction Details</h3>
              </Col>
              <Col>
                <Dropdown
                  menu={{ items: actionMenuItems, onClick: handleAction }}
                  trigger={["click"]}
                  placement="bottomRight"
                >
                  <button
                    type="button"
                    style={{
                      height: 44,
                      padding: "0 20px",
                      borderRadius: 8,
                      border: "1px solid #D0D5DD",
                      background: "#fff",
                      color: "#344054",
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <Settings01 width={16} height={16} color="#344054" />
                    Actions
                    <ChevronDown width={16} height={16} color="#667085" />
                  </button>
                </Dropdown>
              </Col>
            </Row>

            <Form layout="vertical" form={form}>
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Auction Code"
                        tip="The unique code identifying this auction."
                      />
                    }
                    name="auctionCode"
                  >
                    <Input style={{ ...ds }} disabled />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Instrument Type"
                        tip="The type of instrument being auctioned."
                      />
                    }
                    name="instrumentType"
                  >
                    <Input style={{ ...ds }} disabled />
                  </Form.Item>
                </Col>
              </Row>

              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Currency"
                        tip="The currency in which the auction is denominated."
                      />
                    }
                    name="currencyCode"
                  >
                    <Select
                      style={ds}
                      suffixIcon={<ChevronDown color="#667085" />}
                      options={CURRENCY_OPTIONS}
                      placeholder="—"
                      disabled
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Issuing Authority"
                        tip="The authority responsible for issuing the instrument."
                      />
                    }
                    name="issuingAuthority"
                  >
                    <Input style={{ ...ds }} disabled />
                  </Form.Item>
                </Col>
              </Row>

              {/* Auction Date & Settlement Date */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Auction Date"
                        tip="The date on which the auction was conducted."
                      />
                    }
                    name="auctionDate"
                  >
                    <DatePicker
                      style={{
                        width: "100%",
                        height: 44,
                        background: "#F9FAFB",
                        cursor: "not-allowed",
                      }}
                      format="YYYY-MM-DD"
                      disabled
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Settlement Date"
                        tip="The date by which funds and securities are exchanged."
                      />
                    }
                    name="settlementDate"
                  >
                    <DatePicker
                      style={{
                        width: "100%",
                        height: 44,
                        background: "#F9FAFB",
                        cursor: "not-allowed",
                      }}
                      format="YYYY-MM-DD"
                      disabled
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Status */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Status"
                        tip="The current status of this auction."
                      />
                    }
                    name="statusDescription"
                  >
                    <Input style={{ ...ds }} disabled />
                  </Form.Item>
                </Col>
              </Row>
            </Form>

            {/* ── Tabs Section ───────────────────────────────────────────── */}
            <div style={{ borderTop: "1px solid #EAECF0", marginTop: 8 }}>
              <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={tabItems}
                style={{ marginTop: 4 }}
                tabBarStyle={{
                  marginBottom: 0,
                  paddingTop: 12,
                  fontWeight: 500,
                }}
              />
            </div>

            {/* ── Footer ─────────────────────────────────────────────────── */}
            <div
              style={{ borderTop: "1px solid #EAECF0", margin: "20px 0 4px" }}
            />
            <Row justify="end">
              <Col>
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  style={{
                    height: 44,
                    padding: "0 24px",
                    borderRadius: 8,
                    border: "1px solid #D0D5DD",
                    background: "#fff",
                    color: "#344054",
                    fontWeight: 600,
                    fontSize: 14,
                    cursor: "pointer",
                  }}
                >
                  Back
                </button>
              </Col>
            </Row>
          </Card>
        </div>
      </Spin>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  rightBtn: state.RightSideBtn,
  userData: state.AuthenticationData.userData,
});

export default connect(mapStateToProps, { rightAction })(ViewAuction);